export const isScheduleActive = (schedule, currentTime = new Date()) => {
  if (!schedule) return true;
  const scheduleBetweenDates = isTimeBetweenDates(
    currentTime,
    schedule.get('date_start'),
    schedule.get('date_end')
  );
  const daysScheduleActive = isDayScheduleActive(schedule.get('days'), currentTime);
  return scheduleBetweenDates && daysScheduleActive;
};

export const isTimeBetweenDates = (currentTime, startDate, endDate) => {
  if (!startDate && !endDate) return true; // no dates so time valid
  const startDateValid = new Date(currentTime).getTime() >= new Date(startDate).getTime();
  const endDateValid = new Date(currentTime).getTime() < new Date(endDate).getTime();
  if (!startDate) return endDateValid; // no start date, so just validate end date
  if (!endDate) return startDateValid; // no end date, so just validate start date
  return startDateValid && endDateValid; // validate both dates
};

const isDayScheduleActive = (days, currentTime) => {
  if (!days || days.size < 1) return true; // schedule is active if there are no day schedules set
  const currentDaySchedule = getCurrentDaySchedule(days, currentTime);
  if (!currentDaySchedule) return false; // return if no schedule for current day
  const timesAvailable = currentDaySchedule.get('times').find(time => {
    const startTime = timeToDate(time.get('start_time'), currentTime);
    const endTime = timeToDate(time.get('end_time'), currentTime);
    return isTimeBetweenDates(currentTime, startTime, endTime);
  });
  return !!timesAvailable;
};

const getCurrentDay = currentTime => (currentTime.getDay() === 0 ? 7 : currentTime.getDay()); // javascript day for Sunday is 0 but API sends it as 7 so need to convert it

const getCurrentDaySchedule = (days, currentTime) => {
  const currentDay = getCurrentDay(currentTime);
  const currentDaySchedule = days.find(day => day.get('day') === currentDay);
  return currentDaySchedule || false;
};

export const timeToDate = (timeString, date = new Date()) => {
  const newDate = new Date(date.valueOf());
  const timeParts = timeString.split(':');
  newDate.setHours(timeParts[0]);
  newDate.setMinutes(timeParts[1]);
  newDate.setSeconds(timeParts[2]);
  return newDate;
};

export const getNextActiveTime = (schedule, currentTime = new Date()) => {
  if (!schedule) return false;

  const scheduleStartTime = new Date(schedule.get('date_start'));
  const scheduleFinished = !isTimeBetweenDates(currentTime, null, schedule.get('date_end'));
  const scheduleNotStarted = !isTimeBetweenDates(currentTime, schedule.get('date_start'));

  if (scheduleFinished) return false; // schedule is past end date

  if (scheduleNotStarted) currentTime = scheduleStartTime;

  const nextTime = getNextTimeFromDays(schedule.get('days'), currentTime) || scheduleStartTime.getTime();
  const nextTimeIsInSchedule = isTimeBetweenDates(
    nextTime,
    schedule.get('date_start'),
    schedule.get('date_end')
  );

  // We have to check if the time found is in schedule as the schedule could, for example,
  // end tomorrow morning at 9am but the next time would be 10am.
  return nextTimeIsInSchedule ? nextTime : false;
};

const getNextTimeFromDays = (days, currentTime, timeType = 'start_time') => {
  let nextTime = false;
  if (days && days.size) {
    const currentDaySchedule = getCurrentDaySchedule(days, currentTime);
    if (currentDaySchedule) {
      nextTime = getNextTime(currentDaySchedule.get('times'), currentTime, timeType);
    }

    if (!nextTime) {
      // if no time found for current day, check future days in schedule
      const currentWeekday = getCurrentDay(currentTime);
      const sortedDays = sortDays(days, currentWeekday);
      sortedDays.find(day => {
        const dayDiff =
          day.get('day') < currentWeekday
            ? day.get('day') + 7 - currentWeekday
            : day.get('day') - currentWeekday;
        const nextDate = addDays(currentTime, dayDiff);
        nextTime = getNextTime(day.get('times'), nextDate, timeType);
        return nextTime;
      });
    }
  }
  return nextTime;
};

const getNextTime = (times, currentTime, compareTimeType = 'start_time') => {
  const sortedTimes = sortTimes(times, currentTime);
  const nextTimeRange = sortedTimes.find(
    time => timeToDate(time.get(compareTimeType), currentTime).getTime() >= currentTime.getTime() // compare equal times in case schedule set to start at midnight
  );
  return nextTimeRange ? timeToDate(nextTimeRange.get(compareTimeType), currentTime).getTime() : false;
};

export const getInactiveTime = (schedule, currentTime = new Date()) => {
  // get time that schedule is next inactive
  if (!schedule) return false;
  if (isScheduleActive(schedule, currentTime)) {
    const nextTime = getNextTimeFromDays(schedule.get('days'), currentTime, 'end_time');
    const endDate = schedule.get('date_end') && new Date(schedule.get('date_end')).getTime();
    if (endDate && (!nextTime || endDate < nextTime)) return endDate; // if end date present and sooner than the date found or no other date found
    return nextTime || false;
  }
  return currentTime.getTime();
};

export const sortTimes = (times, now) =>
  times.sort((a, b) =>
    timeToDate(a.get('start_time'), now).getTime() < timeToDate(b.get('start_time'), now).getTime() ? -1 : 1
  );

export const addDays = (date, days) => {
  if (days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    result.setHours(0);
    result.setMinutes(0);
    result.setSeconds(0);
    return result;
  }
  return new Date(date);
};

// Sort days from the day after the current weekday increasing and wrapping
// around after sunday, as we only want to go through the days
export const sortDays = (days, weekdayNow) =>
  days.sort((day1, day2) => {
    const sortableId = day => {
      const adjustedDayId = day.get('day') - weekdayNow;
      return adjustedDayId < 1 ? adjustedDayId + 7 : adjustedDayId;
    };
    return sortableId(day1) < sortableId(day2) ? -1 : 1;
  });
